import { createSelector } from '@reduxjs/toolkit';
import { alphaSort } from 'vet-bones/utils';
import { createSelectItem } from 'vet-bones/utils/select';

import {
  ProjectResourceItem,
  ProjectResourceType,
} from 'src/constants/project';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { projectSelector } from 'src/selectors/project';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';

export const projectResourceListSelector = (projectId: string) =>
  createSelector(
    projectSelector(projectId),
    projectNodeDataArraySelector(projectId),
    (project, nodeDataArray) =>
      [
        ...Object.values(project?.fileUploadsById || {})
          .filter((fileUpload) =>
            nodeDataArray.find(
              (nodeData) =>
                isResource(nodeData) &&
                nodeData.key === getResourceKey(fileUpload.id)
            )
          )
          .map<
            ProjectResourceItem & {
              type: ProjectResourceType;
            }
          >((fileUpload) => ({
            text: fileUpload.name,
            value: fileUpload.id,
            type: ProjectResourceType.FILE_UPLOAD,
          })),
        ...Object.values(project?.virtualGraphsById || {})
          .filter((virtualGraph) =>
            nodeDataArray.find(
              (nodeData) =>
                isResource(nodeData) &&
                nodeData.key === getResourceKey(virtualGraph.id)
            )
          )
          .map<
            ProjectResourceItem & {
              type: ProjectResourceType;
            }
          >((fileUpload) => ({
            text: fileUpload.name,
            value: fileUpload.id,
            type: ProjectResourceType.VIRTUAL_GRAPH,
          })),
      ].sort(alphaSort((resourceItem) => resourceItem.text))
  );

export const modelResourceListSelector = (projectId: string) =>
  createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
    Object.values(modelsById)
      .map((projectModel) =>
        createSelectItem(projectModel.name, projectModel.id)
      )
      .sort(alphaSort((resourceItem) => resourceItem.text))
  );
